/**
 * CK | Custom variants (cards) – Kaching Bundles Edition
 *
 * Liest die Kaching Bundles Deal-Bar-Optionen aus dem DOM aus
 * und rendert die CK-Karten damit. Kaching ist die einzige
 * Quelle der Wahrheit fuer Bilder, Titel, Preise und Badges.
 *
 * Ablauf:
 * 1. Warte bis Kaching Widget gerendert ist (polling auf kaching-bundles-block)
 * 2. Lese alle .kaching-bundles__bar Elemente aus
 * 3. Extrahiere: Bild-URL, Titel, Untertitel, Badge, Endpreis, Originalpreis
 * 4. Rendere CK-Karten in data-ck-fb-grid
 * 5. Initialisiere Klick-Handler (Karte auswaehlen + Kaching Deal-Bar synchronisieren)
 */
(() => {
  'use strict';

  // ── Hilfsfunktionen ──────────────────────────────────────────────────────

  /**
   * Liest Daten aus einem Kaching Deal-Bar Element.
   * @param {Element} barEl - .kaching-bundles__bar Element
   * @param {number} index
   * @returns {{index, dealBarId, title, label, badge, imgSrc, imgAlt, finalPrice, originalPrice, subtitle, hasSaving}}
   */
  const parseKachingBar = (barEl, index) => {
    const dealBarId = barEl.getAttribute('data-deal-bar-id') || '';

    const title =
      barEl.querySelector('.kaching-bundles__bar-title')
        ?.textContent?.trim() || '';

    const label =
      barEl.querySelector('.kaching-bundles__bar-label')
        ?.textContent?.trim() || '';

    const badge =
      barEl.querySelector('.kaching-bundles__bar-most-popular__content')
        ?.textContent?.trim() || '';

    const imgEl = barEl.querySelector('.kaching-bundles__bar-image');
    const imgSrc = imgEl?.src || imgEl?.getAttribute('src') || '';
    const imgAlt = imgEl?.alt || title;

    const finalPriceText =
      barEl.querySelector('.kaching-bundles__bar-price')
        ?.textContent?.trim() || '';

    const originalPriceText =
      barEl.querySelector('.kaching-bundles__bar-full-price')
        ?.textContent?.trim() || '';

    const subtitleText =
      barEl.querySelector('.kaching-bundles__bar-subtitle')
        ?.textContent?.trim() || '';

    const hasSaving =
      originalPriceText &&
      finalPriceText &&
      originalPriceText !== finalPriceText;

    return {
      index,
      dealBarId,
      title,
      label,
      badge,
      imgSrc,
      imgAlt,
      finalPrice: finalPriceText,
      originalPrice: originalPriceText,
      subtitle: subtitleText,
      hasSaving,
    };
  };

  /**
   * Rendert eine CK-Karte als HTML-String.
   * @param {object} opt - Ergebnis von parseKachingBar
   * @param {boolean} isSelected
   * @returns {string}
   */
  const renderCard = (opt, isSelected) => {
    const selectedClass = isSelected ? ' is-selected' : '';
    const ariaPressedVal = isSelected ? 'true' : 'false';

    const badgeHtml = opt.badge
      ? `<div class="ck-custom-variants__fb-badge">${escHtml(opt.badge)}</div>`
      : '';

    const imgHtml = opt.imgSrc
      ? `<img src="${escAttr(opt.imgSrc)}" alt="${escAttr(opt.imgAlt)}" loading="lazy">`
      : '';

    const compareHtml = opt.hasSaving
      ? `<span class="ck-custom-variants__price-compare">
           <span class="ck-custom-variants__price-compare-value">${escHtml(opt.originalPrice)}</span>
         </span>`
      : '';

    const savingHtml = opt.subtitle
      ? `<span class="ck-custom-variants__badge">${escHtml(opt.subtitle)}</span>`
      : '';

    return `
<div
  class="ck-custom-variants__card${selectedClass}"
  role="listitem"
  data-ck-variant-card
  data-ck-deal-bar-id="${escAttr(opt.dealBarId)}"
  data-ck-fb-option-index="${opt.index}"
  aria-pressed="${ariaPressedVal}"
>
  <div class="ck-custom-variants__image">
    ${imgHtml}
  </div>
  <div class="ck-custom-variants__content-wrapper">
    ${badgeHtml}
    <div class="ck-custom-variants__content-inner">
      <div class="ck-custom-variants__name">${escHtml(opt.title)}</div>${opt.label ? `\n      <div class="ck-custom-variants__label">${escHtml(opt.label)}</div>` : ''}
      <div class="ck-custom-variants__price">
        <span class="ck-custom-variants__price-current">${escHtml(opt.finalPrice)}</span>
        ${compareHtml}
      </div>
      <div class="ck-custom-variants__bottom">
        ${savingHtml}
      </div>
    </div>
  </div>
</div>`;
  };

  const escHtml = (str) =>
    String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const escAttr = (str) =>
    String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;');

  // ── Kern-Logik ───────────────────────────────────────────────────────────

  /**
   * Initialisiert einen CK-Variants-Container.
   * Wartet bis Kaching Widget gerendert ist, dann befuellt den Grid.
   */
  const ckInit = (ckRoot) => {
    if (!ckRoot || ckRoot.dataset.ckInit === 'true') return;
    ckRoot.dataset.ckInit = 'true';

    const grid = ckRoot.querySelector('[data-ck-fb-grid]');
    if (!grid) return;

    const sectionId = ckRoot.getAttribute('data-ck-section-id');

    // Polling: Warte bis Kaching Widget mit Deal-Bars gerendert ist
    let attempts = 0;
    const maxAttempts = 80; // 80 × 250ms = 20 Sekunden max

    const tryBuild = () => {
      attempts++;
      const kachingBlock = document.querySelector('kaching-bundles-block');
      const dealBars = kachingBlock
        ? Array.from(kachingBlock.querySelectorAll('.kaching-bundles__bar'))
        : [];

      if (dealBars.length === 0) {
        if (attempts < maxAttempts) {
          setTimeout(tryBuild, 250);
        } else {
          // Kaching nicht gefunden – Skeleton entfernen, leerer Zustand
          grid.classList.remove('ck-custom-variants__grid--loading');
          grid.innerHTML = '';
        }
        return;
      }

      // Kaching Deal-Bars gefunden – Karten bauen
      const parsedOptions = dealBars.map((el, i) => parseKachingBar(el, i));

      // Finde die vorausgewaehlte Deal-Bar
      const preselectedIdx = dealBars.findIndex((bar) =>
        bar.classList.contains('kaching-bundles__bar--selected')
      );
      const selectedIdx = preselectedIdx >= 0 ? preselectedIdx : 0;

      const cardsHtml = parsedOptions
        .map((opt, i) => renderCard(opt, i === selectedIdx))
        .join('');

      grid.classList.remove('ck-custom-variants__grid--loading');
      grid.innerHTML = cardsHtml;

      // Klick-Handler initialisieren
      initCardClicks(ckRoot, grid, sectionId);

      // Buy-Button Preis initial setzen
      syncBuyButtonPrice(sectionId, parsedOptions[selectedIdx]);
    };

    setTimeout(tryBuild, 300);
  };

  /**
   * Initialisiert Klick-Handler auf den CK-Karten.
   */
  const initCardClicks = (ckRoot, grid, sectionId) => {
    grid.addEventListener('click', (e) => {
      const card = e.target.closest('[data-ck-variant-card]');
      if (!card) return;

      // Alle Karten deselektieren
      grid.querySelectorAll('[data-ck-variant-card]').forEach((c) => {
        c.classList.remove('is-selected');
        c.setAttribute('aria-pressed', 'false');
      });

      // Geklickte Karte selektieren
      card.classList.add('is-selected');
      card.setAttribute('aria-pressed', 'true');

      // Buy-Button Preis aktualisieren
      const dealBarId = card.getAttribute('data-ck-deal-bar-id');
      const kachingBlock = document.querySelector('kaching-bundles-block');
      if (kachingBlock && dealBarId) {
        const barEl = kachingBlock.querySelector(
          `.kaching-bundles__bar[data-deal-bar-id="${CSS.escape(dealBarId)}"]`
        );
        if (barEl) {
          const optIndex = parseInt(card.getAttribute('data-ck-fb-option-index') || '0', 10);
          const opt = parseKachingBar(barEl, optIndex);
          syncBuyButtonPrice(sectionId, opt);
        }
      }
    });
  };

  /**
   * Aktualisiert den Preis im CK-Buy-Button anhand der Kaching Option.
   */
  const syncBuyButtonPrice = (sectionId, opt) => {
    if (!opt) return;

    const buyButtonRoot = sectionId
      ? document.querySelector(`[data-ck-buy-button][data-ck-section-id="${CSS.escape(sectionId)}"]`)
      : document.querySelector('[data-ck-buy-button]');

    if (!buyButtonRoot) return;

    const priceCurrent = buyButtonRoot.querySelector('[data-ck-buy-button-price-current]');
    const compareWrap = buyButtonRoot.querySelector('[data-ck-buy-button-price-compare]');
    const compareValue = buyButtonRoot.querySelector('[data-ck-buy-button-price-compare-value]');

    if (priceCurrent) {
      priceCurrent.textContent = opt.finalPrice;
    }

    if (compareWrap && compareValue) {
      if (opt.hasSaving) {
        compareValue.textContent = opt.originalPrice;
        compareWrap.hidden = false;
      } else {
        compareWrap.hidden = true;
      }
    }
  };

  // ── Boot ─────────────────────────────────────────────────────────────────

  const ckBoot = () => {
    document.querySelectorAll('[data-ck-custom-variants]').forEach(ckInit);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ckBoot);
  } else {
    ckBoot();
  }
})();
